import React from 'react';
import useData from "../../../hooks/useData";
import {useTheme} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {
  alphaColor, BarChart,
  BarTimeChart,
  getBarChartOptions,
  getBarTimeChartOptions,
  getTimeChartOptions,
  TimeChart
} from "../../../components/charts";
import {
  SiteFinancialCreditLineDataResponse,
  SiteFinancialBeforeAfterResponse,
  SiteForecastDataResponse, SiteMeteringCommodityPeaksDataResponse, SiteMeteringCommodityVolumesDataResponse,
} from "../../../services";
import {themeOptions} from '../../../config/theme/theme';
import {addDays, differenceInDays} from "date-fns";

function getXScale(dateLabels) {
  const first = dateLabels[0]
  const last = dateLabels.at(-1)
  // Count calendar days instead of 24h blocks, so a 25h DST day still counts as one day
  const fullDays = differenceInDays(last, first)
  const diffDays = addDays(first, fullDays) < last ? fullDays + 1 : fullDays;

  if (diffDays <= 1) {
    return 'hour'
  } else if (diffDays <= 7) {
    return 'day'
  } else {
    return 'week'
  }
}

interface ImbalancePricesChartProps {
  data: any;
  isEpexControlled?: boolean;
}

export function ImbalancePricesChart({ data, isEpexControlled = false }: ImbalancePricesChartProps) {
  const theme: any = useTheme();
  const dateLabels = data["readings"].map(el => new Date(1000 * el.utctime))

  const datasets = [];

  // Only show imbalance price if not EPEX controlled
  if (!isEpexControlled) {
    datasets.push({
      type: 'bar',
      fill: true,
      label: 'Onbalansprijs',
      data: data["readings"].map(el => el["imbPrice"] === null ? null : Math.round(el["imbPrice"] * 100) / 100),
      backgroundColor: theme.palette.imbPrice.main,
      borderColor: theme.palette.imbPrice.main,
      borderWidth: 0,
      borderRadius: 5,
      cubicInterpolationMode: 'monotone',
      pointStyle: false,
      order: 2
    });
  }

  // Always show EPEX price
  datasets.push({
    type: "line",
    fill: false,
    label: 'Epexprijs',
    data: data["readings"].map(el => el["epex"] === null ? null : Math.round(el["epex"] * 100) / 100),
    backgroundColor: theme.palette.epex.main,
    borderColor: theme.palette.epex.main,
    borderWidth: 3,
    cubicInterpolationMode: 'monotone',
    pointStyle: false,
    order: 1,
    spanGaps: true
  });

  const chartData = {
    labels: data["readings"].map(el => new Date(1000 * el.utctime)),
    datasets: datasets,
  };

  const options: any = getTimeChartOptions(' €/MWh');
  options["scales"]["x"]["time"]["unit"] = getXScale(dateLabels)
  return <TimeChart data={chartData} options={options}/>
}

export function FlexChart() {
  const {data} = useData();
  const {t} = useTranslation();
  const dateLabels = data["readings"].map(el => new Date(1000 * el.utctime))

  const flexData = data["readings"].map(el => (el["flexCharge"] === null) || (el["flexDischarge"] === null) ? null : Math.round(((el["flexCharge"] - el["flexDischarge"]) / 1000) * 100) / 100);
  const chargeData = flexData.map(value => value !== null && value >= 0 ? value : null);
  const dischargeData = flexData.map(value => value !== null && value < 0 ? value : null);

  const chartData = {
    labels: data["readings"].map(el => new Date(1000 * el.utctime)),
    datasets: [
      {
        fill: true,
        label: t('dashboard.technical.flexCharge'),
        data: chargeData,
        backgroundColor: themeOptions.palette.flex.secondary,
        cubicInterpolationMode: 'monotone',
        pointStyle: false,
        order: 1,
        minBarLength: 1,
      },
      {
        fill: true,
        label: t('dashboard.technical.flexDischarge'),
        data: dischargeData,
        backgroundColor: themeOptions.palette.flex.secondary,
        cubicInterpolationMode: 'monotone',
        pointStyle: false,
        order: 2,
        minBarLength: 1,
      }
    ],
  };

  const options = getBarTimeChartOptions(false, " kW")
  options["scales"]["x"]["time"]["unit"] = getXScale(dateLabels)
  options["plugins"]["tooltip"]["filter"] = (tooltipItem) => tooltipItem.raw !== null;
  options["plugins"]["legend"]["display"] = false;
  return <BarTimeChart data={chartData} options={options}/>
}

export function SOCChart() {
  const {data} = useData();
  const dateLabels = data["readings"].map(el => new Date(1000 * el.utctime))

  const chartData = {
    labels: data["readings"].map(el => new Date(1000 * el.utctime)),
    datasets: [
      {
        fill: true,
        label: 'State Of Charge',
        data: data["readings"].map(el => el["soc"] === null ? null : Math.round(el["soc"] * 100) / 100),
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, context.chart.height);
          gradient.addColorStop(0, alphaColor(themeOptions.palette.soc.main, 0.9));
          gradient.addColorStop(1, alphaColor(themeOptions.palette.soc.main, 0.1));
          return gradient;
        },
        borderWidth: 2,
        borderColor: themeOptions.palette.soc.main,
        cubicInterpolationMode: 'monotone',
        pointStyle: false,
        order: 2
      }
    ],
  };

  var options = getTimeChartOptions("%");
  options["scales"]["x"]["time"]["unit"] = getXScale(dateLabels)
  options["scales"]["y"]["min"] = 0
  options["scales"]["y"]["max"] = 100
  options["plugins"]["legend"]["display"] = false;

  return <TimeChart data={chartData} options={options}/>
}

export function ConsumptionChart() {
  const {data} = useData();
  const dateLabels = data["readings"].map(el => new Date(1000 * el.utctime))

  const chartData = {
    labels: data["readings"].map(el => new Date(1000 * el.utctime)),
    datasets: [
      {
        fill: true,
        label: 'Consumption',
        data: data["readings"].map(el => el["consumption"] === null ? null : Math.round((el["consumption"] / 1000) * 100) / 100),
        backgroundColor: themeOptions.palette.consumption.main,//'rgba(255,255,255,0.4)',
        cubicInterpolationMode: 'monotone',
        pointStyle: false,
        order: 2,
        minBarLength: 1,
      }
    ],
  };

  const options = getBarTimeChartOptions(false, " kW");
  options["scales"]["x"]["time"]["unit"] = getXScale(dateLabels)
  options["plugins"]["legend"]["display"] = false;

  const productionData = data["readings"].map(el => el["production"] === null ? null : Math.round((el["production"] / 1000) * 100) / 100)
  const productionMax = Math.max(Math.max(...productionData))
  const consumptionMax = Math.max(Math.max(...chartData.datasets[0].data))
  const absMax = Math.max(Math.abs(consumptionMax), Math.abs(productionMax))

  options.scales.y.max = Math.ceil(absMax);

  return <BarTimeChart data={chartData} options={options}/>
}

export function ProductionChart() {
  const {data} = useData();
  const dateLabels = data["readings"].map(el => new Date(1000 * el.utctime))

  const chartData = {
    labels: data["readings"].map(el => new Date(1000 * el.utctime)),
    datasets: [
      {
        fill: true,
        label: 'Effectieve productie',
        data: data["readings"].map(el => el["uncurtailedProduction"] === null ? null : Math.round((el["uncurtailedProduction"] / 1000) * 100) / 100),
        backgroundColor: themeOptions.palette.production.main,//'rgba(255,255,255,0.4)',
        cubicInterpolationMode: 'monotone',
        pointStyle: false,
        order: 1,
        minBarLength: 1,
      },
      {
        fill: true,
        label: 'Uitgeschakelde productie bij ongunstige marktprijzen',
        data: data["readings"].map(el => el["curtailedProduction"] === null ? null : Math.round((el["curtailedProduction"] / 1000) * 100) / 100),
        backgroundColor: themeOptions.palette.production.secondary,//'rgba(255,255,255,0.4)',
        cubicInterpolationMode: 'monotone',
        pointStyle: false,
        order: 2,
        minBarLength: 0,
      }
    ],
  };

  const options = getBarTimeChartOptions(false, " kW");
  options["scales"]["x"]["time"]["unit"] = getXScale(dateLabels)

  const consumptionData = data["readings"].map(el => el["consumption"] === null ? null : Math.round((el["consumption"] / 1000) * 100) / 100)
  const consumptionMax = Math.max(Math.max(...consumptionData))
  const productionMax = Math.max(Math.max(...chartData.datasets[0].data))
  const absMax = Math.max(Math.abs(consumptionMax), Math.abs(productionMax))

  options.scales.y.max = Math.ceil(absMax);

  return <BarTimeChart data={chartData} options={options}/>
}

export function GridChart() {
  const {data} = useData();
  const {t} = useTranslation();
  const dateLabels = data["readings"].map(el => new Date(1000 * el.utctime))

  const gridData = data["readings"].map(el => el["grid"] === null ? null : Math.round((el["grid"] / 1000) * 100) / 100);
  const offtakeData = gridData.map(value => value !== null && value >= 0 ? value : null);
  const injectionData = gridData.map(value => value !== null && value < 0 ? value : null);

  const chartData = {
    labels: data["readings"].map(el => new Date(1000 * el.utctime)),
    datasets: [
      {
        fill: true,
        label: t('dashboard.technical.gridOfftake'),
        data: offtakeData,
        backgroundColor: themeOptions.palette.grid.main,
        cubicInterpolationMode: 'monotone',
        pointStyle: false,
        order: 1,
        minBarLength: 1,
      },
      {
        fill: true,
        label: t('dashboard.technical.gridInjection'),
        data: injectionData,
        backgroundColor: themeOptions.palette.grid.secondary,
        cubicInterpolationMode: 'monotone',
        pointStyle: false,
        order: 2,
        minBarLength: 1,
      }
    ],
  };

  const options = getBarTimeChartOptions(false, " kW");
  options["scales"]["x"]["time"]["unit"] = getXScale(dateLabels)
  options["plugins"]["tooltip"]["filter"] = (tooltipItem) => tooltipItem.raw !== null;
  return <BarTimeChart data={chartData} options={options}/>
}

interface CreditLineChartProperties {
  data: Array<SiteFinancialCreditLineDataResponse>;
}

export function CreditLineChart(props: CreditLineChartProperties) {
  const dateLabels = props.data.map((el: SiteFinancialCreditLineDataResponse) => new Date(1000 * el.utctime))

  const chartData = {
    labels: props.data.map((el: SiteFinancialCreditLineDataResponse) => new Date(1000 * el.utctime)),
    datasets: [
      {
        fill: true,
        label: 'Kredietlijn',
        data: props.data.map((el: SiteFinancialCreditLineDataResponse) => el.credit === null ? null : Math.round(el.credit * 100) / 100),
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, context.chart.height);
          const baseColor = themeOptions.palette.secondary.main;
          // Convert rgb(r, g, b) to rgba(r, g, b, alpha)
          const colorWithAlpha = (alpha) => baseColor.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
          gradient.addColorStop(0, colorWithAlpha(0.9));
          gradient.addColorStop(1, colorWithAlpha(0.1));
          return gradient;
        },
        borderWidth: 2,
        borderColor: themeOptions.palette.secondary.main,
        cubicInterpolationMode: 'monotone',
        pointStyle: false,
        order: 2,
        minBarLength: 1,
      }
    ],
  };

  const options = getTimeChartOptions("€ ", true);
  // options["scales"]["x"]["time"]["unit"] = getXScale(dateLabels)
  options["scales"]["x"]["time"]["unit"] = "day"
  options["plugins"]["legend"]["display"] = false;
  return <TimeChart data={chartData} options={options}/>
}

interface ForecastActualChartProperties {
  data: Array<SiteForecastDataResponse>;
}

export function ForecastActualChart(props: ForecastActualChartProperties) {
  const dateLabels = props.data.map(el => new Date(1000 * el.utctime))

  const chartData = {
    labels: props.data.map(el => new Date(1000 * el.utctime)),
    datasets: [
      {
        type: 'line',
        fill: false,
        label: 'Forecast',
        data: props.data.map((el: SiteForecastDataResponse) => el.forecast !== null ? Math.round((el.forecast / 1000) * 100) / 100 : null),
        backgroundColor: themeOptions.palette.forecast.main,
        borderColor: themeOptions.palette.forecast.main,
        borderWidth: 2,
        cubicInterpolationMode: 'monotone',
        pointStyle: false,
        order: 1
      },
      {
        type: 'line',
        fill: false,
        label: 'Nominated',
        data: props.data.map((el: SiteForecastDataResponse) => el.nominated !== null ? Math.round((el.nominated / 1000) * 100) / 100 : null),
        backgroundColor: themeOptions.palette.forecast.secondary,
        borderColor: themeOptions.palette.forecast.secondary,
        borderWidth: 2,
        cubicInterpolationMode: 'monotone',
        pointStyle: false,
        order: 2,
      },
      {
        fill: true,
        label: 'Actual',
        data: props.data.map((el: SiteForecastDataResponse) => el.actual ? Math.round((el.actual / 1000) * 100) / 100 : null),
        backgroundColor: themeOptions.palette.secondary.main,//'rgba(255,255,255,0.4)',
        cubicInterpolationMode: 'monotone',
        pointStyle: false,
        order: 3,
        minBarLength: 1,
      }
    ],
  };

  const options = getBarTimeChartOptions(false, " kWh");
  options["scales"]["x"]["time"]["unit"] = getXScale(dateLabels)
  options["scales"]["y"]["stacked"] = false
  // options["plugins"]["legend"]["display"] = false;

  return <BarTimeChart data={chartData} options={options}/>
}

interface CommodityVolumesChartProperties {
  data: Array<SiteMeteringCommodityVolumesDataResponse>;
}

export function CommodityVolumesChart(props: CommodityVolumesChartProperties) {
  const chartData = {
    labels: props.data.map((el: SiteMeteringCommodityVolumesDataResponse) => el.invoiceMode),
    datasets: [
      {
        fill: true,
        label: 'Grid Afname',
        data: props.data.map((el: SiteMeteringCommodityVolumesDataResponse) => el.gridOfftake === null ? null : Math.round((el.gridOfftake / 1000) * 100) / 100),
        backgroundColor: themeOptions.palette.consumption.main,//'rgba(255,255,255,0.4)',
        cubicInterpolationMode: 'monotone',
        pointStyle: false,
        order: 1,
        minBarLength: 1,
      },
      {
        fill: true,
        label: 'Grid Injectie',
        data: props.data.map((el: SiteMeteringCommodityVolumesDataResponse) => el.gridInjection === null ? null : Math.round((el.gridInjection / 1000) * 100) / 100),
        backgroundColor: themeOptions.palette.production.main,//'rgba(255,255,255,0.4)',
        cubicInterpolationMode: 'monotone',
        pointStyle: false,
        order: 2,
        minBarLength: 1,
      }
    ],
  };

  const options = getBarChartOptions(false, " MWh");
  return <BarChart data={chartData} options={options}/>
}
interface BeforeAfterInvoicesChartProperties {
  data: Array<SiteFinancialBeforeAfterResponse>;
}

export function BeforeAfterInvoicesChart(props: BeforeAfterInvoicesChartProperties) {
  // Filter out months where either value is 0
  const filteredData = props.data.filter((el: SiteFinancialBeforeAfterResponse) => 
    el.before_client_total !== 0 && el.after_total !== 0
  );

  const dateLabels = filteredData.map((el: SiteFinancialBeforeAfterResponse) => new Date(1000 * el.period))

  const chartData = {
    labels: filteredData.map((el: SiteFinancialBeforeAfterResponse) => new Date(1000 * el.period)),
    datasets: [
      {
        fill: true,
        label: 'Voor',
        data: filteredData.map((el: SiteFinancialBeforeAfterResponse) => el.before_client_total === null ? null : Math.round(el.before_client_total * 100) / 100),
        // backgroundColor: themeOptions.palette.flex.main,
        backgroundColor: '#B8B8B8',
        cubicInterpolationMode: 'monotone',
        pointStyle: false,
        order: 1,
        minBarLength: 1,
      },
      {
        fill: true,
        label: 'Na',
        data: filteredData.map((el: SiteFinancialBeforeAfterResponse) => el.after_total === null ? null : Math.round(el.after_total * 100) / 100),
        backgroundColor: themeOptions.palette.primary.main,
        cubicInterpolationMode: 'monotone',
        pointStyle: false,
        order: 2,
        minBarLength: 1,
      }
    ],
  };

  const options = getBarTimeChartOptions(true, "€ ");
  options["scales"]["x"]["time"]["unit"] = "month"
  options["scales"]["x"]["time"]["tooltipFormat"] = 'MM/yyyy';
  options["scales"]["x"]["stacked"] = false;
  options["scales"]["y"]["stacked"] = false;
  return <BarTimeChart data={chartData} options={options}/>
}
interface CommodityPeaksChartProperties {
  data: Array<SiteMeteringCommodityPeaksDataResponse>;
}

export function CommodityPeaksChart(props: CommodityPeaksChartProperties) {
  const chartData = {
    labels: props.data.map((el: SiteMeteringCommodityPeaksDataResponse) => el.invoiceMode),
    datasets: [
      {
        fill: true,
        label: 'Capaciteit piek (kW)',
        data: props.data.map((el: SiteMeteringCommodityPeaksDataResponse) => el.capacityPeak === null ? null : Math.round(el.capacityPeak * 100) / 100),
        backgroundColor: themeOptions.palette.consumption.main,//'rgba(255,255,255,0.4)',
        cubicInterpolationMode: 'monotone',
        pointStyle: false,
        order: 1,
        minBarLength: 1,
      },
      {
        fill: true,
        label: 'Overtollig vermogen (kVA)',
        data: props.data.map((el: SiteMeteringCommodityPeaksDataResponse) => el.excessPower === null ? null : Math.round(el.excessPower * 100) / 100),
        backgroundColor: themeOptions.palette.forecast.main,//'rgba(255,255,255,0.4)',
        cubicInterpolationMode: 'monotone',
        pointStyle: false,
        order: 2,
        minBarLength: 1,
      }
    ],
  };

  const options = getBarChartOptions(false, "");
  return <BarChart data={chartData} options={options}/>
}